import React, { useEffect } from 'react';
import { Switch, Route, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';

import './App.css';

import 'react-toastify/dist/ReactToastify.css';
import './assets/css/tokens.css';
import './assets/css/style.css';
import Spinner from './views/Spinner/Spinner';

import CustomerInfoForm from './components/CustomerInfoForm/CustomerInfoForm';
import HomePage from './views/Home/Home';
import CreateJobPage from './components/CreateJobPage/CreateJobPage';
import ViewJobPage from './components/ViewJobPage/ViewJobPage';
import AllJobsPage from './components/AllJobsPage/AllJobsPage';
import DownloadJobPage from './components/DownloadJobPage/DownloadJobPage';
import TestPDF from './components/TestPDF/TestPDF';
import CreateBarcode from './components/CreateBarcode/CreateBarcode';
import ViewBarcode from './components/ViewBarcode/ViewBarcode';
import DownloadBarcodePage from './components/CreateBarcode/CreateBarcodePDF';
import CftCalculatorPage from './components/CftCalculatorPage/CftCalculatorPage';
import QuotationPricingPage from './components/QuotationPricingPage/QuotationPricingPage';
import QuotationsPage from './components/QuotationsPage/QuotationsPage';
import Sidebar from './views/Sidebar/Sidebar';
import Header from './views/Header/Header';
import ProductTutorial from './components/ProductTutorial/ProductTutorial';
import InteractiveDesktopTour from './components/InteractiveDesktopTour/InteractiveDesktopTour';
import TenantBrandingBootstrap from './components/TenantBrandingBootstrap/TenantBrandingBootstrap';
import Settings from './views/Settings/Settings';
import Locked from './views/AccountLocked/AccountLocked';
import Signup from './views/Signup/Signup';
import VerifyOTP from './views/VerifyOTP/VerifyOTP';
import VerMessage from './views/VerifiedMessage/VerifiedMessage';
import VerEmail from './views/VerifyEmail/VerifyEmail';
import ResendOTP from './views/ResendOTP/ResendOTP';
import PublicQuotationWizard from './components/PublicQuotationWizard/PublicQuotationWizard';
import PublicCftEntry from './components/PublicQuotationWizard/PublicCftEntry';
import VerifyEmailTokenPage from './views/Auth/VerifyEmailTokenPage';
import NotFound from './views/NotFound/NotFound';
import AccountBanners from './components/AccountBanners/AccountBanners';

/** Stable lazy references — inline `React.lazy(() => import())` in render breaks Route identity and remounts on every parent re-render (e.g. Redux updates), resetting form state. */
const AceLandingLazy = React.lazy(() => import('./views/AceLanding/AceLanding'));
const AceBlogLazy = React.lazy(() => import('./views/AceBlog/AceBlog'));
const SignInPageLazy = React.lazy(() => import('./views/Auth/SignInPage'));
const ForgotPasswordPageLazy = React.lazy(() => import('./views/Auth/ForgotPasswordPage'));
const ResetPasswordTokenPageLazy = React.lazy(() => import('./views/Auth/ResetPasswordTokenPage'));
const AcceptInvitePageLazy = React.lazy(() => import('./views/Auth/AcceptInvitePage'));
const OrganizationOnboardingLazy = React.lazy(() =>
  import('./views/Onboarding/OrganizationOnboarding')
);
const OrganizationSettingsLazy = React.lazy(() =>
  import('./views/Settings/OrganizationSettings')
);
const OperationsSearchPageLazy = React.lazy(() =>
  import('./views/Operations/OperationsSearchPage')
);
const OperationsContactsPageLazy = React.lazy(() =>
  import('./views/Operations/OperationsContactsPage')
);
const OperationsTeamPageLazy = React.lazy(() => import('./views/Operations/OperationsTeamPage'));
const CheckoutPageLazy = React.lazy(() => import('./views/Checkout/CheckoutPage'));
const PublicJobTrackingLazy = React.lazy(() =>
  import('./views/PublicJobTracking/PublicJobTracking')
);

toast.configure();

function isPublicQuotePath(pathname) {
  if (pathname === '/public/cft') return true;
  if (pathname === '/quote-request') return true;
  if (/^\/public\/cft\/[^/]+$/.test(pathname)) return true;
  if (/^\/quote-request\/[^/]+$/.test(pathname)) return true;
  return false;
}

function isPublicTrackingPath(pathname) {
  return /^\/track\/[^/]+$/.test(pathname);
}

function sanitizeNextPath(nextPath) {
  const v = String(nextPath || "").trim();
  if (!v.startsWith("/")) return "/";
  if (v.startsWith("//")) return "/";
  return v;
}

/** Routes that do not require auth (landing, quote wizard URLs, signup / verify flows). */
function isPublicPath(pathname) {
  if (isPublicQuotePath(pathname)) return true;
  if (pathname === '/') return true;
  if (pathname === '/blog') return true;
  if (
    pathname === '/sign-in' ||
    pathname === '/login' ||
    pathname === '/sign-up' ||
    pathname === '/signup' ||
    pathname === '/forgot-password' ||
    pathname.startsWith('/accept-invite/') ||
    pathname.startsWith('/reset-password') ||
    pathname === '/verify-otp' ||
    pathname === '/resend-otp' ||
    pathname === '/verify-email' ||
    pathname.startsWith('/verify-email/') ||
    pathname.startsWith('/verified-message/')
  ) {
    return true;
  }
  return false;
}

function App() {
  const history = useHistory();
  const location = useLocation();

  const { authUser, logout } = useSelector((state) => state.Auth);

  useEffect(() => {
    if (!authUser && isPublicPath(location.pathname)) return;
    const requestedPath = `${location.pathname}${location.search || ""}`;
    if (
      location.pathname !== '/sign-in' &&
      location.pathname !== '/login' &&
      (authUser === null || authUser === undefined || authUser === 'undefined')
    ) {
      history.push(`/sign-in?next=${encodeURIComponent(requestedPath)}`);
    } else if (
      (location.pathname === '/sign-in' || location.pathname === '/login') &&
      authUser &&
      !logout
    ) {
      const params = new URLSearchParams(location.search || "");
      const nextPath = sanitizeNextPath(params.get("next"));
      history.push(nextPath || '/');
    }
  }, [authUser, history, location.pathname, location.search, logout]);

  let mainContent = (
    <Switch>
      <Route exact path="/" component={AceLandingLazy} />
      <Route exact path="/blog" component={AceBlogLazy} />
      <Route exact path="/sign-in" component={SignInPageLazy} />
      <Route exact path="/login" render={() => <Redirect to="/sign-in" />} />
      <Route exact path="/sign-up" component={Signup} />
      <Route exact path="/signup" render={() => <Redirect to="/sign-up" />} />
      <Route exact path="/forgot-password" component={ForgotPasswordPageLazy} />
      <Route exact path="/reset-password/:token" component={ResetPasswordTokenPageLazy} />
      <Route exact path="/accept-invite/:token" component={AcceptInvitePageLazy} />
      <Route exact path="/reset-password" render={() => <Redirect to="/forgot-password" />} />
      <Route path='/verify-otp' component={VerifyOTP} exact/>
      <Route path='/resend-otp' component={ResendOTP} exact/>
      <Route path='/verified-message/:id/:email' component={VerMessage} exact/>
      <Route exact path="/verify-email/:token" component={VerifyEmailTokenPage} />
      <Route path='/verify-email' component={VerEmail} exact/>
      <Route component={NotFound} />
    </Switch>
  )

  if(authUser) {
    mainContent = (
      <Switch>
          <Route path='/customer' component={CustomerInfoForm} />
          <Route path="/blog" exact component={AceBlogLazy} />
          <Route path='/' component={HomePage}  exact/>
          <Route path='/create-job' component={CreateJobPage} exact />
          <Route path='/edit-job/:jobId' component={CreateJobPage} />
          <Route path='/view-job/:jobId' component={ViewJobPage} />
          <Route path='/jobs' component={AllJobsPage} exact />
          <Route path='/jobs/edit/:jobId' component={CreateJobPage} />
          <Route path='/download-job' component={DownloadJobPage} />
          <Route path='/download-barcode' component={DownloadBarcodePage} />
          <Route path='/test-pdf' component={TestPDF} />
          <Route path='/create-barcode' component={CreateBarcode} />
          <Route path='/barcode' component={ViewBarcode} exact/>
          <Route path='/cft-calculator' component={CftCalculatorPage} exact />
          <Route path='/quotations' component={QuotationsPage} exact />
          <Route path='/quotations/new' component={CftCalculatorPage} exact />
          <Route path='/quotations/:quotationId/pricing' component={QuotationPricingPage} exact />
          <Route path='/quotations/:quotationId/edit' component={CftCalculatorPage} exact />
          <Route path='/quotations/:quotationId' component={CftCalculatorPage} exact />
          <Route path="/profile" exact render={() => <Redirect to="/settings-profile?tab=profile" />} />
          <Route path='/settings-profile' component={Settings} exact/>
          <Route path="/onboarding" exact component={OrganizationOnboardingLazy} />
          <Route path="/settings/organization" exact component={OrganizationSettingsLazy} />
          <Route path='/subscribe' exact component={CheckoutPageLazy} />
          <Route path="/operations/search" exact component={OperationsSearchPageLazy} />
          <Route path="/operations/contacts" exact component={OperationsContactsPageLazy} />
          <Route path="/operations/team" exact component={OperationsTeamPageLazy} />
          <Route path='/account-locked' component={Locked} exact/>
          <Route component={NotFound} />
      </Switch>
    )
  }

  return (
    <React.Suspense fallback={<Spinner/>}>
      <Switch>
        <Route path="/public/cft" exact component={PublicCftEntry} />
        <Route path="/quote-request" exact component={PublicCftEntry} />
        <Route path="/public/cft/:token" exact component={PublicQuotationWizard} />
        <Route path="/quote-request/:token" exact component={PublicQuotationWizard} />
        <Route path="/track/:token" exact component={PublicJobTrackingLazy} />
        <Route
          render={() => (
            <React.Fragment>
              {authUser && (
                <>
                  <TenantBrandingBootstrap />
                  <Header />
                  <Sidebar />
                  <ProductTutorial />
                  <InteractiveDesktopTour />
                </>
              )}
              <div
                style={{
                  marginLeft: authUser ? 'var(--app-shell-offset, 96px)' : 0,
                  paddingTop: authUser ? 72 : 0,
                  minHeight: authUser ? 'calc(100vh - 72px)' : undefined,
                }}
              >
                {authUser && <AccountBanners />}
                {mainContent}
              </div>
            </React.Fragment>
          )}
        />
      </Switch>
      <div style={{ position: 'fixed', top: '76px', right: '16px', zIndex: 2000 }}>
        <ToastContainer containerId='appLayoutToaster' />
      </div>
    </React.Suspense>
  );
}

export default App;